export type BillingAddressData = {
  streetAddress?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
};

export type BillingAddressField =
  keyof BillingAddressData;

export type BillingAddressFieldErrors = Partial<
  Record<BillingAddressField, string>
>;

const requiredBillingAddressFields: Array<{
  key: BillingAddressField;
  label: string;
  errorMessage: string;
}> = [
  {
    key: 'streetAddress',
    label: 'Street address',
    errorMessage: 'Please enter a street address',
  },
  {
    key: 'city',
    label: 'City',
    errorMessage: 'Please enter a city',
  },
  {
    key: 'province',
    label: 'Province/state',
    errorMessage: 'Please enter a province/state',
  },
  {
    key: 'postalCode',
    label: 'Postal code',
    errorMessage: 'Please enter a postal code',
  },
  {
    key: 'country',
    label: 'Country',
    errorMessage: 'Please enter a country',
  },
];

const formatMissingFields = (fields: string[]) => {
  const normalizedFields = fields.map((field, index) =>
    index === 0 ? field : field.toLowerCase()
  );

  if (normalizedFields.length <= 1) {
    return normalizedFields[0] || '';
  }

  const lastField = normalizedFields.at(-1);
  const otherFields = normalizedFields.slice(0, -1);

  return `${otherFields.join(', ')} and ${lastField}`;
};

export const validateBillingAddressField = (
  field: BillingAddressField,
  value?: string
) => {
  if (value?.trim()) {
    return '';
  }

  return (
    requiredBillingAddressFields.find(
      ({ key }) => key === field
    )?.errorMessage || 'This field is required'
  );
};

export const validateBillingAddress = (
  address: BillingAddressData
) => {
  const missingFields: string[] = [];
  const fieldErrors: BillingAddressFieldErrors = {};

  requiredBillingAddressFields.forEach(
    ({ key, label, errorMessage }) => {
      if (!address[key]?.trim()) {
        missingFields.push(label);
        fieldErrors[key] = errorMessage;
      }
    }
  );

  const isValid = missingFields.length === 0;
  const formattedMissingFields =
    formatMissingFields(missingFields);

  return {
    isValid,
    missingFields,
    fieldErrors,
    errorMessage: isValid
      ? ''
      : `${formattedMissingFields} ${
          missingFields.length === 1 ? 'is' : 'are'
        } missing. Please complete the billing address.`,
  };
};